import { useCallback, useEffect, useMemo, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import UnderlineTabNav from '@/components/UnderlineTabNav'
import {
  TableToolbar,
  applySorts,
  type FieldDefinition,
  type SortSpec,
} from '@/tableControls'
import { toggleSingleColumnSort } from '@/pages/fixed-assets/fixedAssetsTableFilters'
import { downloadCsvFile } from '@/utils/warehouseTableHelpers'
import {
  listCaseSnapshots,
  listCases,
  type CaseListItem,
} from '@/services/projections'
import type { History, SchemaEntry, View } from './engine/types'
import {
  buildCompareModel,
  monthAccuracy,
  multiMonthAccuracy,
  type CompareModel,
} from './compare/compareModel'
import { isFavorable } from './compare/favorability'
import { formatCompareDelta } from './GridCell'
import { DRIVER_LABELS, gridDisplayUnit, type GridDisplayUnit, type PickerDriver } from './gridClasses'
import { driverInputUnit } from './driverUtils'
import {
  formatByUnit,
  formatDate,
  formatMonthLabel,
  formatPercent,
} from './projectionsFormat'
import { useDialogFocusTrap } from './useDialogFocusTrap'

const VIEW_TABS = [
  { id: 'river_oaks', label: 'River Oaks' },
  { id: 'highland_park', label: 'Highland Park' },
  { id: 'ga', label: 'G&A' },
  { id: 'consolidated', label: 'Consolidated' },
]

const SCORECARD_STORE = [
  'is.net_revenue',
  'is.gross_margin_pct',
  'is.ebitdar',
  'is.ebitdar_margin_pct',
  'is.net_income_loss',
] as const

const SCORECARD_CONSOL = [
  ...SCORECARD_STORE,
  'is.ebitda',
  'is.ebitda_margin_pct',
] as const

const BAND_KEYS = new Set(['is.operational_data', 'is.income_statement'])

type ScopeId = 'scorecard' | 'income' | 'operational' | 'bs_cf'

const SCOPE_OPTIONS: { id: ScopeId; label: string }[] = [
  { id: 'scorecard', label: 'Scorecard' },
  { id: 'income', label: 'Income statement' },
  { id: 'operational', label: 'Operational' },
  { id: 'bs_cf', label: 'Balance sheet & cash flow' },
]

const MONTH_ABBREV = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

const EMPTY_MESSAGE =
  'No projection has been captured for this case yet. Snapshots are written the first time Update Historicals closes a month this case projected.'

export type ProjectionAccuracyModalProps = {
  open: boolean
  onClose: () => void
  initialCaseId: number | null
  lastClosedMonth: string
  history: History | null
  schema: SchemaEntry[]
}

function formatMonthYear(yyyyMm: string): string {
  const [y, m] = yyyyMm.split('-')
  const mi = Number(m) - 1
  if (!y || mi < 0 || mi > 11) {
    return yyyyMm
  }
  return `${MONTH_ABBREV[mi]} ${y}`
}

function lineLabelForKey(schema: SchemaEntry[], key: string): string {
  return schema.find((e) => e.key === key)?.label.trim() ?? key
}

function operationalKeys(schema: SchemaEntry[]): string[] {
  const opIdx = schema.findIndex((e) => e.key === 'is.operational_data')
  const isIdx = schema.findIndex((e) => e.key === 'is.income_statement')
  if (opIdx < 0 || isIdx < 0 || isIdx <= opIdx) {
    return []
  }
  return schema
    .slice(opIdx + 1, isIdx)
    .filter((e) => e.kind !== 'header')
    .map((e) => e.key)
}

function incomeKeys(schema: SchemaEntry[]): string[] {
  const isIdx = schema.findIndex((e) => e.key === 'is.income_statement')
  if (isIdx < 0) {
    return []
  }
  const opKeys = new Set(operationalKeys(schema))
  return schema
    .slice(isIdx + 1)
    .filter((e) => e.tab === 'IS' && e.kind !== 'header' && !opKeys.has(e.key) && !BAND_KEYS.has(e.key))
    .map((e) => e.key)
}

function bsCfKeys(schema: SchemaEntry[], modelKeys: Set<string>): string[] {
  const fromSchema = schema
    .filter((e) => e.tab === 'BS' && e.kind !== 'header')
    .map((e) => e.key)
  const cfKeys = [...modelKeys].filter((k) => k.startsWith('cf.'))
  return [...new Set([...fromSchema, ...cfKeys])]
}

function keysOnModel(model: CompareModel, view: View, month: string | 'all'): Set<string> {
  const set = new Set<string>()
  const months = month === 'all' ? model.months : [month]
  for (const m of months) {
    for (const k of Object.keys(model.byMonth[m]?.[view] ?? {})) {
      set.add(k)
    }
  }
  return set
}

function scopeLineKeys(
  scope: ScopeId,
  view: View,
  schema: SchemaEntry[],
  model: CompareModel,
  month: string | 'all',
): string[] {
  let candidates: readonly string[]
  if (scope === 'scorecard') {
    candidates = view === 'consolidated' ? SCORECARD_CONSOL : SCORECARD_STORE
  } else if (scope === 'operational') {
    candidates = operationalKeys(schema)
  } else if (scope === 'income') {
    candidates = incomeKeys(schema)
  } else {
    candidates = bsCfKeys(schema, keysOnModel(model, view, month))
  }
  const present = keysOnModel(model, view, month)
  return candidates.filter((k) => present.has(k))
}

type SingleMonthRow = {
  lineKey: string
  lineLabel: string
  projected: number | null
  actual: number | null
  delta: number | null
  deltaPct: number | null
  driverText: string
  absDelta: number
}

type MultiMonthRow = {
  lineKey: string
  lineLabel: string
  months: number
  meanPct: number | null
  meanAbsPct: number | null
  worstMonth: string | null
  mape: number
}

const SINGLE_MONTH_FIELDS: FieldDefinition[] = [
  { key: 'lineLabel', label: 'Line', type: 'text', sortable: true, accessor: (row) => (row as SingleMonthRow).lineLabel },
  { key: 'projected', label: 'Projected', type: 'number', sortable: true, accessor: (row) => (row as SingleMonthRow).projected },
  { key: 'actual', label: 'Actual', type: 'number', sortable: true, accessor: (row) => (row as SingleMonthRow).actual },
  { key: 'delta', label: 'Δ', type: 'number', sortable: true, accessor: (row) => (row as SingleMonthRow).delta },
  { key: 'absDelta', label: 'Δ', type: 'number', sortable: true, accessor: (row) => (row as SingleMonthRow).absDelta },
  {
    key: 'deltaPct',
    label: 'Δ %',
    type: 'number',
    sortable: true,
    accessor: (row) => (row as SingleMonthRow).deltaPct,
  },
  {
    key: 'driverText',
    label: 'Driver at capture',
    type: 'text',
    sortable: true,
    accessor: (row) => (row as SingleMonthRow).driverText,
  },
]

const MULTI_MONTH_FIELDS: FieldDefinition[] = [
  { key: 'lineLabel', label: 'Line', type: 'text', sortable: true, accessor: (row) => (row as MultiMonthRow).lineLabel },
  { key: 'months', label: 'Months', type: 'number', sortable: true, accessor: (row) => (row as MultiMonthRow).months },
  { key: 'meanPct', label: 'Bias %', type: 'number', sortable: true, accessor: (row) => (row as MultiMonthRow).meanPct },
  { key: 'mape', label: 'MAPE %', type: 'number', sortable: true, accessor: (row) => (row as MultiMonthRow).meanAbsPct },
  {
    key: 'worstMonth',
    label: 'Worst month',
    type: 'text',
    sortable: true,
    accessor: (row) => (row as MultiMonthRow).worstMonth,
  },
]

function unitForKey(schema: SchemaEntry[], lineKey: string): GridDisplayUnit {
  const entry = schema.find((e) => e.key === lineKey)
  return gridDisplayUnit(lineKey, entry?.unit ?? 'usd')
}

function driverDisplay(
  driver: { active: string | null; value: number | null } | null | undefined,
): string {
  if (!driver?.active) {
    return '—'
  }
  const active = driver.active
  const label =
    active in DRIVER_LABELS ? DRIVER_LABELS[active as PickerDriver] : active
  const unit =
    active in DRIVER_LABELS ? driverInputUnit(active as PickerDriver) : ('usd' as const)
  const valueText =
    driver.value != null ? formatByUnit(unit, driver.value) : '—'
  return `${label} ${valueText}`
}

function ariaSortFor(field: string, sortSpecs: SortSpec[]): 'ascending' | 'descending' | 'none' {
  const spec = sortSpecs.find((s) => s.field === field)
  if (!spec) {
    return 'none'
  }
  return spec.direction === 'asc' ? 'ascending' : 'descending'
}

export default function ProjectionAccuracyModal({
  open,
  onClose,
  initialCaseId,
  lastClosedMonth,
  history,
  schema,
}: ProjectionAccuracyModalProps) {
  const dialogRef = useDialogFocusTrap(open, onClose)
  const [cases, setCases] = useState<CaseListItem[]>([])
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null)
  const [compareModel, setCompareModel] = useState<CompareModel | null>(null)
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState<View>('river_oaks')
  const [scope, setScope] = useState<ScopeId>('scorecard')
  const [selectedMonth, setSelectedMonth] = useState<string | 'all'>('all')
  const [singleSortSpecs, setSingleSortSpecs] = useState<SortSpec[]>([
    { field: 'absDelta', direction: 'desc' },
  ])
  const [multiSortSpecs, setMultiSortSpecs] = useState<SortSpec[]>([
    { field: 'mape', direction: 'desc' },
  ])
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }
    let cancelled = false
    setLoading(true)
    void listCases()
      .then((all) => {
        if (cancelled) {
          return
        }
        const withSnapshots = all.filter((c) => c.snapshot_months.length > 0)
        setCases(withSnapshots)
        const defaultId =
          initialCaseId != null && withSnapshots.some((c) => c.id === initialCaseId)
            ? initialCaseId
            : (withSnapshots[0]?.id ?? null)
        setSelectedCaseId(defaultId)
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [open, initialCaseId])

  useEffect(() => {
    if (!open || selectedCaseId == null || history == null) {
      setCompareModel(null)
      return
    }
    let cancelled = false
    setLoading(true)
    void listCaseSnapshots(selectedCaseId, true)
      .then((snapshots) => {
        if (cancelled) {
          return
        }
        if (snapshots.length === 0) {
          setCompareModel(null)
          return
        }
        const model = buildCompareModel(snapshots, history, lastClosedMonth, schema)
        setCompareModel(model)
        setSelectedMonth(model.months.at(-1) ?? 'all')
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [open, selectedCaseId, history, lastClosedMonth, schema])

  useEffect(() => {
    if (view !== 'consolidated' && scope === 'bs_cf') {
      setScope('scorecard')
    }
  }, [view, scope])

  const caseName = cases.find((c) => c.id === selectedCaseId)?.name ?? ''
  const isAllMonths = selectedMonth === 'all'
  const showEmpty =
    cases.length === 0 || history == null || compareModel == null || compareModel.months.length === 0

  const lineKeys = useMemo(() => {
    if (!compareModel || showEmpty) {
      return []
    }
    return scopeLineKeys(scope, view, schema, compareModel, selectedMonth)
  }, [compareModel, showEmpty, scope, view, schema, selectedMonth])

  const singleRows = useMemo((): SingleMonthRow[] => {
    if (!compareModel || isAllMonths || typeof selectedMonth !== 'string') {
      return []
    }
    const month = selectedMonth
    return lineKeys
      .map((lineKey) => {
        const cell = compareModel.byMonth[month]?.[view]?.[lineKey]
        const delta = cell?.delta ?? null
        return {
          lineKey,
          lineLabel: lineLabelForKey(schema, lineKey),
          projected: cell?.projected ?? null,
          actual: cell?.actual ?? null,
          delta,
          deltaPct: cell?.deltaPct ?? null,
          driverText: driverDisplay(cell?.driver),
          absDelta: delta != null ? Math.abs(delta) : -1,
        }
      })
      .filter((row) => row.projected != null && row.actual != null && row.delta != null)
  }, [compareModel, isAllMonths, selectedMonth, lineKeys, view, schema])

  const multiRows = useMemo((): MultiMonthRow[] => {
    if (!compareModel || !isAllMonths) {
      return []
    }
    const stats = multiMonthAccuracy(compareModel, view, lineKeys)
    return lineKeys
      .filter((k) => stats[k])
      .map((lineKey) => {
        const s = stats[lineKey]!
        return {
          lineKey,
          lineLabel: lineLabelForKey(schema, lineKey),
          months: s.months,
          meanPct: s.meanPct,
          meanAbsPct: s.meanAbsPct,
          worstMonth: s.worstMonth,
          mape: s.meanAbsPct ?? -1,
        }
      })
  }, [compareModel, isAllMonths, lineKeys, view, schema])

  const sortedSingleRows = useMemo(
    () => applySorts(singleRows, singleSortSpecs, SINGLE_MONTH_FIELDS),
    [singleRows, singleSortSpecs],
  )

  const sortedMultiRows = useMemo(
    () => applySorts(multiRows, multiSortSpecs, MULTI_MONTH_FIELDS),
    [multiRows, multiSortSpecs],
  )

  const summaryText = useMemo(() => {
    if (showEmpty) {
      return null
    }
    if (isAllMonths) {
      return `All captured months · ${caseName} · ${sortedMultiRows.length} lines`
    }
    const month = selectedMonth as string
    const acc = monthAccuracy(compareModel!, view, month, lineKeys)
    const saved = formatDate(compareModel!.source.caseSavedAt)
    const captured = formatDate(compareModel!.source.capturedAt)
    let text = `${formatMonthYear(month)} · ${caseName} · projection saved ${saved}, captured ${captured} · ${acc.linesCompared} lines compared`
    if (acc.linesCompared === 0) {
      return text
    }
    const medianPart =
      acc.medianAbsPct != null ? formatPercent(acc.medianAbsPct) : '—'
    text += ` · median |Δ%| ${medianPart}`
    if (acc.largestMiss) {
      const unit = unitForKey(schema, acc.largestMiss.lineKey)
      const deltaStr = formatCompareDelta(unit, acc.largestMiss.delta)
      const pctPart =
        acc.largestMiss.deltaPct != null
          ? ` (${formatPercent(acc.largestMiss.deltaPct)})`
          : ''
      text += ` · largest miss: ${lineLabelForKey(schema, acc.largestMiss.lineKey)} ${deltaStr}${pctPart}`
    }
    return text
  }, [
    showEmpty,
    isAllMonths,
    caseName,
    sortedMultiRows.length,
    selectedMonth,
    compareModel,
    view,
    lineKeys,
    schema,
  ])

  const caseOptions: SelectOption[] = useMemo(
    () => cases.map((c) => ({ id: String(c.id), label: c.name })),
    [cases],
  )

  const monthOptions: SelectOption[] = useMemo(() => {
    if (!compareModel) {
      return []
    }
    return [
      ...compareModel.months.map((m) => ({ id: m, label: formatMonthLabel(m) })),
      { id: 'all', label: 'All captured months' },
    ]
  }, [compareModel])

  const handleSingleSort = useCallback((field: string) => {
    setSingleSortSpecs((prev) => toggleSingleColumnSort(prev, field))
  }, [])

  const handleMultiSort = useCallback((field: string) => {
    setMultiSortSpecs((prev) => toggleSingleColumnSort(prev, field))
  }, [])

  const handleExportCsv = useCallback(() => {
    setIsExporting(true)
    try {
      if (isAllMonths) {
        const header = ['Line', 'Months', 'Bias %', 'MAPE %', 'Worst month']
        const lines = sortedMultiRows.map((row) => [
          row.lineLabel,
          String(row.months),
          formatPercent(row.meanPct),
          formatPercent(row.meanAbsPct),
          row.worstMonth ? formatMonthLabel(row.worstMonth) : '—',
        ])
        downloadCsvFile(
          'projection-accuracy.csv',
          [header, ...lines].map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n'),
        )
      } else {
        const header = ['Line', 'Projected', 'Actual', 'Δ', 'Δ %', 'Driver at capture']
        const lines = sortedSingleRows.map((row) => {
          const unit = unitForKey(schema, row.lineKey)
          return [
            row.lineLabel,
            formatByUnit(unit, row.projected),
            formatByUnit(unit, row.actual),
            formatCompareDelta(unit, row.delta),
            row.deltaPct != null ? formatPercent(row.deltaPct) : '—',
            row.driverText,
          ]
        })
        downloadCsvFile(
          'projection-accuracy.csv',
          [header, ...lines].map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n'),
        )
      }
    } finally {
      setIsExporting(false)
    }
  }, [isAllMonths, sortedMultiRows, sortedSingleRows, schema])

  if (!open) {
    return null
  }

  const sortSpecs = isAllMonths ? multiSortSpecs : singleSortSpecs
  const onSort = isAllMonths ? handleMultiSort : handleSingleSort

  function sortButton(label: string, field: string, className = '') {
    return (
      <th key={field} className={className}>
        <button
          type="button"
          className="font-medium text-gray-700 hover:text-gray-900"
          aria-sort={ariaSortFor(field, sortSpecs)}
          onClick={() => onSort(field)}
        >
          {label}
        </button>
      </th>
    )
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="projection-accuracy-title"
        className="flex max-h-[85vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <h3 id="projection-accuracy-title" className="text-base font-semibold text-gray-900">
            Projection accuracy
          </h3>
          <Button variant="outline" iconOnly aria-label="Close" onClick={onClose}>
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading && compareModel == null ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : showEmpty ? (
            <p className="text-sm text-gray-700">{EMPTY_MESSAGE}</p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div aria-label="Accuracy case">
                  <SearchableSelect
                    options={caseOptions}
                    value={selectedCaseId != null ? String(selectedCaseId) : null}
                    onChange={(id) => setSelectedCaseId(id != null ? Number(id) : null)}
                    placeholder="Case"
                    disabled={caseOptions.length === 0}
                  />
                </div>
                <div aria-label="Accuracy month">
                  <SearchableSelect
                    options={monthOptions}
                    value={selectedMonth}
                    onChange={(id) => setSelectedMonth((id as string) ?? 'all')}
                    placeholder="Month"
                  />
                </div>
              </div>

              <UnderlineTabNav
                tabs={VIEW_TABS}
                activeId={view}
                onChange={(id) => setView(id as View)}
                ariaLabel="Projection view"
              />

              <div
                className="inline-flex min-w-max gap-2 rounded-lg bg-gray-200 p-1"
                role="group"
                aria-label="Accuracy scope"
              >
                {SCOPE_OPTIONS.map((opt) => {
                  const disabled = opt.id === 'bs_cf' && view !== 'consolidated'
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => setScope(opt.id)}
                      className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all disabled:cursor-not-allowed disabled:opacity-40 ${
                        scope === opt.id
                          ? 'bg-white text-brand-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>

              {summaryText ? (
                <p className="text-sm text-gray-700">{summaryText}</p>
              ) : null}

              <TableToolbar
                filterCount={0}
                sortCount={sortSpecs.length}
                showFilters={false}
                showSort={false}
                showColumns={false}
                onToggleFilters={() => {}}
                onToggleSort={() => {}}
                onToggleColumns={() => {}}
                exportOptions={[
                  {
                    id: 'csv',
                    label: 'CSV',
                    onClick: handleExportCsv,
                    disabled: sortedSingleRows.length === 0 && sortedMultiRows.length === 0,
                  },
                ]}
                defaultExportId="csv"
                isExporting={isExporting}
                exportDisabled={sortedSingleRows.length === 0 && sortedMultiRows.length === 0}
              />

              <div className="overflow-x-auto">
                <table className="table-forecast w-full text-sm">
                  <thead>
                    {isAllMonths ? (
                      <tr>
                        {sortButton('Line', 'lineLabel', 'text-left')}
                        {sortButton('Months', 'months', 'text-right tabular-nums')}
                        {sortButton('Bias %', 'meanPct', 'text-right tabular-nums')}
                        {sortButton('MAPE %', 'mape', 'text-right tabular-nums')}
                        {sortButton('Worst month', 'worstMonth', 'text-left')}
                      </tr>
                    ) : (
                      <tr>
                        {sortButton('Line', 'lineLabel', 'text-left')}
                        {sortButton('Projected', 'projected', 'text-right tabular-nums')}
                        {sortButton('Actual', 'actual', 'text-right tabular-nums')}
                        {sortButton('Δ', 'absDelta', 'text-right tabular-nums')}
                        {sortButton('Δ %', 'deltaPct', 'text-right tabular-nums')}
                        <th className="text-left">Driver at capture</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {isAllMonths
                      ? sortedMultiRows.map((row) => (
                          <tr key={row.lineKey} data-line-key={row.lineKey}>
                            <td>{row.lineLabel}</td>
                            <td className="text-right tabular-nums">{row.months}</td>
                            <td className="text-right tabular-nums">{formatPercent(row.meanPct)}</td>
                            <td className="text-right tabular-nums">{formatPercent(row.meanAbsPct)}</td>
                            <td>{row.worstMonth ? formatMonthLabel(row.worstMonth) : '—'}</td>
                          </tr>
                        ))
                      : sortedSingleRows.map((row) => {
                          const unit = unitForKey(schema, row.lineKey)
                          const fav = isFavorable(row.lineKey, row.delta)
                          const deltaClass =
                            fav === true
                              ? 'text-green-700'
                              : fav === false
                                ? 'text-red-700'
                                : 'text-inherit'
                          return (
                            <tr key={row.lineKey} data-line-key={row.lineKey}>
                              <td>{row.lineLabel}</td>
                              <td className="text-right tabular-nums">
                                {formatByUnit(unit, row.projected)}
                              </td>
                              <td className="text-right tabular-nums">
                                {formatByUnit(unit, row.actual)}
                              </td>
                              <td className={`text-right tabular-nums ${deltaClass}`}>
                                {formatCompareDelta(unit, row.delta)}
                              </td>
                              <td className="text-right tabular-nums">
                                {row.deltaPct != null ? formatPercent(row.deltaPct) : '—'}
                              </td>
                              <td>{row.driverText}</td>
                            </tr>
                          )
                        })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
